import { ApiError } from '@gorila-shared-ui/components';
import { ApiEndpoint } from '../types/api';
import { HistoricAlarm, HistoricDoorState, HistoricState, HistoricTelemetry } from '../types/currentState';
import { EvidenceMedia } from '../types/evidences';
import { NotificationType } from '../types/notification';
import { Path, PathItem, PathItemByGroup } from '../types/paths';
import {
  AlarmsReport,
  ConsumptionMedia,
  ConsumptionReport,
  DeliveryReport,
  DoorStateReport,
  EvidencesReport,
  PathsReport,
  PositionReport,
  TelemetryReport,
} from '../types/reports';
import { deleteRequest, getRequest, postRequest } from './api';
import { notificationTypesUrl } from './urls/notificationUrls';
import {
  consumptionFilesUrl,
  consumptionReportFileUrl,
  consumptionReportUrl,
  deliveryReportFilesUrl,
  deliveryReportFileUrl,
  doorStateByGroupReportUrl,
  doorStateReportFilesUrl,
  doorStateReportFileUrl,
  doorStateReportUrl,
  historicAlarmsByGroupReportUrl,
  historicAlarmsFilesUrl,
  historicAlarmsReportFileUrl,
  historicAlarmsReportUrl,
  historicEvidenceByGroupReportUrl,
  historicEvidenceFilesUrl,
  historicEvidenceReportUrl,
  historicEvidencesReportFileUrl,
  historicStateByGroupReportUrl,
  historicStateFilesUrl,
  historicStateReportFileUrl,
  historicStateReportUrl,
  pathsByGroupReportUrl,
  pathsFilesUrl,
  pathsReportFileUrl,
  pathsReportUrl,
  pathUrl,
  telemetryByGroupReportUrl,
  telemetryFilesUrl,
  telemetryReportFileUrl,
  telemetryReportUrl,
} from './urls/reportUrls';

export const getHistoricState = async (
  clientId: string,
  assetId: string,
  startDate: number,
  endDate?: number,
  subClientId?: string,
): Promise<{ historicStates?: HistoricState[]; error?: ApiError }> => {
  const queryParams: { [key: string]: any } = {
    clientId,
    subClientId,
    allItems: true,
    startDate,
  };
  if (endDate) {
    queryParams['endDate'] = endDate;
  }
  const response = await getRequest(historicStateReportUrl(assetId), {
    queryParams,
  });
  return {
    historicStates: response?.data?.items,
    error: response?.error,
  };
};

export const getHistoricStateByGroup = async (
  clientId: string,
  groupId: string,
  startDate: number,
  endDate?: number,
  subClientId?: string,
): Promise<{ historicStates?: HistoricState[]; error?: ApiError }> => {
  const queryParams: { [key: string]: any } = {
    clientId,
    subClientId,
    allItems: true,
    startDate,
  };
  if (endDate) {
    queryParams['endDate'] = endDate;
  }
  const response = await getRequest(historicStateByGroupReportUrl(groupId), {
    queryParams,
  });
  return {
    historicStates: response?.data?.items,
    error: response?.error,
  };
};

export const getHistoricReportFiles = async (
  clientId: string,
  page: number,
  subClientId?: string,
  assetId?: string,
): Promise<{ reports?: PositionReport[]; error?: ApiError; total?: number }> => {
  const response = await getRequest(historicStateFilesUrl, {
    queryParams: { clientId, subClientId, page, assetId },
  });

  return {
    total: response?.data?.total,
    reports: response?.data?.items,
    error: response?.error,
  };
};

export const requestHistoricReportFile = async (
  assetId: string,
  startDate: number,
  endDate: number,
  clientId: string,
  subClientId?: string,
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(historicStateFilesUrl, {
    body: { clientId, subClientId, assetId, startDate, endDate },
  });

  return {
    error: response?.error,
  };
};

export const requestHistoricByGroupReportFile = async (
  groupId: string,
  startDate: number,
  endDate: number,
  clientId: string,
  subClientId?: string,
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(historicStateFilesUrl, {
    body: { clientId, subClientId, groupId, startDate, endDate },
  });

  return {
    error: response?.error,
  };
};

export const getHistoricReportFile = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ url?: string; error?: ApiError }> => {
  const response = await getRequest(historicStateReportFileUrl(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    url: response?.data?.url,
    error: response?.error,
  };
};

export const deleteHistoricReportFile = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ error?: ApiError }> => {
  const response = await deleteRequest(historicStateReportFileUrl(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    error: response?.error,
  };
};

export const getNotificationTypes = async (): Promise<{
  notificationTypes?: NotificationType[];
  error?: ApiError;
  total?: number;
}> => {
  const response = await getRequest(
    notificationTypesUrl,
    {
      queryParams: {
        exclude_autoreport: false,
      },
    },
    undefined,
    ApiEndpoint.core,
  );

  return {
    notificationTypes: response?.data,
    error: response?.error,
  };
};

export const getHistoricTelemetry = async (
  clientId: string,
  assetId: string,
  startDate: number,
  endDate?: number,
  subClientId?: string,
): Promise<{ historicTelemetry?: HistoricTelemetry[]; error?: ApiError }> => {
  const queryParams: { [key: string]: any } = {
    clientId,
    subClientId,
    allItems: true,
    startDate,
  };
  if (endDate) {
    queryParams['endDate'] = endDate;
  }
  const response = await getRequest(telemetryReportUrl(assetId), {
    queryParams,
  });
  return {
    historicTelemetry: response?.data?.items,
    error: response?.error,
  };
};

export const getHistoricTelemetryByGroup = async (
  clientId: string,
  groupId: string,
  startDate: number,
  endDate?: number,
  subClientId?: string,
): Promise<{ historicTelemetry?: HistoricTelemetry[]; error?: ApiError }> => {
  const queryParams: { [key: string]: any } = {
    clientId,
    subClientId,
    allItems: true,
    startDate,
  };
  if (endDate) {
    queryParams['endDate'] = endDate;
  }
  const response = await getRequest(telemetryByGroupReportUrl(groupId), {
    queryParams,
  });
  return {
    historicTelemetry: response?.data?.items,
    error: response?.error,
  };
};

export const getTelemetryReportFiles = async (
  clientId: string,
  page: number,
  subClientId?: string,
  assetId?: string,
): Promise<{ reports?: TelemetryReport[]; error?: ApiError; total?: number }> => {
  const response = await getRequest(telemetryFilesUrl, {
    queryParams: { clientId, subClientId, page, assetId },
  });

  return {
    total: response?.data?.total,
    reports: response?.data?.items,
    error: response?.error,
  };
};

export const requestTelemetryReportFile = async (
  assetId: string,
  startDate: number,
  endDate: number,
  clientId: string,
  subClientId?: string,
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(telemetryFilesUrl, {
    body: { clientId, subClientId, assetId, startDate, endDate },
  });

  return {
    error: response?.error,
  };
};

export const requestTelemetryByGroupReportFile = async (
  groupId: string,
  startDate: number,
  endDate: number,
  clientId: string,
  subClientId?: string,
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(telemetryFilesUrl, {
    body: { clientId, subClientId, groupId, startDate, endDate },
  });

  return {
    error: response?.error,
  };
};

export const getTelemetryReportFile = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ url?: string; error?: ApiError }> => {
  const response = await getRequest(telemetryReportFileUrl(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    url: response?.data?.url,
    error: response?.error,
  };
};

export const deleteTelemetryReport = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ error?: ApiError }> => {
  const response = await deleteRequest(telemetryReportFileUrl(reportId), { queryParams: { clientId, subClientId } });

  return {
    error: response?.error,
  };
};

export const getPaths = async (
  clientId: string,
  assetId: string,
  page: number,
  subClientId?: string,
  startDate?: number | null,
  endDate?: number | null,
  minSeconds?: number,
  minMeters?: number,
): Promise<{ paths?: PathItem[]; error?: ApiError; total?: number }> => {
  const response = await getRequest(pathsReportUrl(assetId), {
    queryParams: {
      clientId,
      subClientId,
      page,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      minSeconds,
      minMeters,
    },
  });

  return {
    total: response?.data?.total,
    paths: response?.data?.items,
    error: response?.error,
  };
};

export const getPathsByGroup = async (
  clientId: string,
  groupId: string,
  page: number,
  subClientId?: string,
  startDate?: number | null,
  endDate?: number | null,
  minSeconds?: number,
  minMeters?: number,
): Promise<{ paths?: PathItemByGroup[]; error?: ApiError; total?: number }> => {
  const response = await getRequest(pathsByGroupReportUrl(groupId), {
    queryParams: {
      clientId,
      subClientId,
      page,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      minSeconds,
      minMeters,
    },
  });

  return {
    total: response?.data?.total,
    paths: response?.data?.items,
    error: response?.error,
  };
};

export const getPath = async (
  pathId: string,
  clientId: string,
  subClientId?: string,
  assetId?: string,
): Promise<{ path?: Path; error?: ApiError }> => {
  const response = await getRequest(pathUrl(pathId), {
    queryParams: { clientId, subClientId, assetId },
  });

  return {
    path: response?.data,
    error: response?.error,
  };
};

export const getPathsReportFiles = async (
  clientId: string,
  page: number,
  subClientId?: string,
  assetId?: string,
): Promise<{ reports?: PathsReport[]; error?: ApiError; total?: number }> => {
  const response = await getRequest(pathsFilesUrl, {
    queryParams: { clientId, subClientId, page, assetId },
  });

  return {
    total: response?.data?.total,
    reports: response?.data?.items,
    error: response?.error,
  };
};

export const getPathsReportFile = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ url?: string; error?: ApiError }> => {
  const response = await getRequest(pathsReportFileUrl(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    url: response?.data?.url,
    error: response?.error,
  };
};

export const requestPathsReportFile = async (
  assetId: string,
  startDate: number,
  endDate: number,
  clientId: string,
  subClientId?: string,
  filters?: { [key: string]: string | number },
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(pathsFilesUrl, {
    body: { clientId, subClientId, assetId, startDate, endDate, filters },
  });

  return {
    error: response?.error,
  };
};

export const requestPathsByGroupReportFile = async (
  groupId: string,
  startDate: number,
  endDate: number,
  clientId: string,
  subClientId?: string,
  filters?: { [key: string]: string | number },
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(pathsFilesUrl, {
    body: { clientId, subClientId, groupId, startDate, endDate, filters },
  });

  return {
    error: response?.error,
  };
};

export const deletePathsReportFile = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ error?: ApiError }> => {
  const response = await deleteRequest(pathsReportFileUrl(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    error: response?.error,
  };
};

export const getHistoricConsumption = async (
  clientId: string,
  page: number,
  dateKey: string,
  subClientId?: string,
): Promise<{ historicConsumption?: ConsumptionMedia[]; total?: number; error?: ApiError }> => {
  const queryParams: { [key: string]: any } = {
    clientId,
    subClientId,
    page,
    dateKey,
  };
  const response = await getRequest(consumptionReportUrl, {
    queryParams,
  });
  return {
    historicConsumption: response?.data?.items,
    total: response?.data?.total,
    error: response?.error,
  };
};

export const getComsumptionReportFiles = async (
  clientId: string,
  page: number,
  subClientId?: string,
): Promise<{ reports?: ConsumptionReport[]; error?: ApiError; total?: number }> => {
  const response = await getRequest(consumptionFilesUrl, {
    queryParams: { clientId, subClientId, page },
  });

  return {
    total: response?.data?.total,
    reports: response?.data?.items,
    error: response?.error,
  };
};

export const getConsumptionReportFile = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ url?: string; error?: ApiError }> => {
  const response = await getRequest(consumptionReportFileUrl(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    url: response?.data?.url,
    error: response?.error,
  };
};

export const requestConsumptionReportFile = async (
  clientId: string,
  dateKey: string,
  subClientId?: string,
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(consumptionFilesUrl, {
    body: { clientId, subClientId, dateKey },
  });

  return {
    error: response?.error,
  };
};

export const deleteConsumptionReportFile = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ error?: ApiError }> => {
  const response = await deleteRequest(consumptionReportFileUrl(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    error: response?.error,
  };
};

export const getHistoricDoorState = async (
  clientId: string,
  assetId: string,
  startDate: number,
  endDate?: number,
  subClientId?: string,
): Promise<{ historicDoor?: HistoricDoorState[]; error?: ApiError }> => {
  const queryParams: { [key: string]: any } = {
    clientId,
    subClientId,
    allItems: true,
    startDate,
  };
  if (endDate) {
    queryParams['endDate'] = endDate;
  }
  const response = await getRequest(doorStateReportUrl(assetId), {
    queryParams,
  });
  return {
    historicDoor: response?.data?.items,
    error: response?.error,
  };
};

export const getHistoricDoorStateByGroup = async (
  clientId: string,
  groupId: string,
  startDate: number,
  endDate?: number,
  subClientId?: string,
): Promise<{ historicDoor?: HistoricDoorState[]; error?: ApiError }> => {
  const queryParams: { [key: string]: any } = {
    clientId,
    subClientId,
    allItems: true,
    startDate,
  };
  if (endDate) {
    queryParams['endDate'] = endDate;
  }
  const response = await getRequest(doorStateByGroupReportUrl(groupId), {
    queryParams,
  });
  return {
    historicDoor: response?.data?.items,
    error: response?.error,
  };
};

export const requestDoorStateReportFile = async (
  assetId: string,
  startDate: number,
  endDate: number,
  clientId: string,
  subClientId?: string,
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(doorStateReportFilesUrl, {
    body: { clientId, subClientId, assetId, startDate, endDate },
  });

  return {
    error: response?.error,
  };
};

export const requestDoorStateByGroupReportFile = async (
  groupId: string,
  startDate: number,
  endDate: number,
  clientId: string,
  subClientId?: string,
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(doorStateReportFilesUrl, {
    body: { clientId, subClientId, groupId, startDate, endDate },
  });

  return {
    error: response?.error,
  };
};

export const getDoorStateReportFiles = async (
  clientId: string,
  page: number,
  subClientId?: string,
  assetId?: string,
): Promise<{ reports?: DoorStateReport[]; error?: ApiError; total?: number }> => {
  const response = await getRequest(doorStateReportFilesUrl, {
    queryParams: { clientId, subClientId, page, assetId },
  });

  return {
    total: response?.data?.total,
    reports: response?.data?.items,
    error: response?.error,
  };
};

export const getDoorStateReportFile = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ url?: string; error?: ApiError }> => {
  const response = await getRequest(doorStateReportFileUrl(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    url: response?.data?.url,
    error: response?.error,
  };
};

export const deleteDoorStateReport = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ error?: ApiError }> => {
  const response = await deleteRequest(doorStateReportFileUrl(reportId), { queryParams: { clientId, subClientId } });

  return {
    error: response?.error,
  };
};

// historic evidence report
export const getStreamingMedia = async (
  clientId: string,
  assetId: string,
  startDate: number,
  endDate: number,
  subClientId?: string,
  mediaType?: string,
  origin?: string,
  alarmType?: number,
): Promise<{ data?: EvidenceMedia[]; error?: ApiError }> => {
  const queryParams = {
    clientId,
    subClientId,
    startDate,
    endDate,
    mediaType,
    origin,
    alarmType,
  };
  const response = await getRequest(historicEvidenceReportUrl(assetId), {
    queryParams,
  });
  return {
    data: response?.data,
    error: response?.error,
  };
};

export const getStreamingMediaByGroup = async (
  clientId: string,
  groupId: string,
  startDate: number,
  endDate: number,
  subClientId?: string,
  mediaType?: string,
  origin?: string,
  alarmType?: number,
): Promise<{ data?: EvidenceMedia[]; error?: ApiError }> => {
  const queryParams = {
    clientId,
    subClientId,
    startDate,
    endDate,
    mediaType,
    origin,
    alarmType,
  };
  const response = await getRequest(historicEvidenceByGroupReportUrl(groupId), {
    queryParams,
  });
  return {
    data: response?.data,
    error: response?.error,
  };
};

// delivery report
export const getDeliveryReports = async ({
  clientId,
  assetId,
  date,
  page,
  subClientId,
  groupId,
}: {
  clientId: string;
  date: string;
  page: number;
  assetId?: string;
  subClientId?: string;
  groupId?: string;
}): Promise<{
  reports?: DeliveryReport[];
  allDevicesDocument?: DeliveryReport;
  total?: number;
  error?: ApiError;
}> => {
  const queryParams: { [key: string]: any } = {
    clientId,
    subClientId,
    assetId,
    groupId,
    page,
    dateKey: date,
  };
  const response = await getRequest(deliveryReportFilesUrl, {
    queryParams,
  });

  return {
    total: response?.data?.total,
    reports: response?.data?.items,
    allDevicesDocument: response?.data?.allDevicesDocument,
    error: response?.error,
  };
};

export const getDeliveryReportFile = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ url?: string; error?: ApiError }> => {
  const response = await getRequest(deliveryReportFileUrl(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    url: response?.data?.url,
    error: response?.error,
  };
};

export const getEvidencesReportFiles = async (
  clientId: string,
  page: number,
  subClientId?: string,
  assetId?: string,
): Promise<{ reports?: EvidencesReport[]; error?: ApiError; total?: number }> => {
  const response = await getRequest(historicEvidenceFilesUrl, {
    queryParams: { clientId, subClientId, page, assetId },
  });

  return {
    total: response?.data?.total,
    reports: response?.data?.items,
    error: response?.error,
  };
};

export const getEvidencesReportFile = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ url?: string; error?: ApiError }> => {
  const response = await getRequest(historicEvidencesReportFileUrl(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    url: response?.data?.url,
    error: response?.error,
  };
};

export const deleteEvidencesReportFile = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ error?: ApiError }> => {
  const response = await deleteRequest(historicEvidencesReportFileUrl(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    error: response?.error,
  };
};

export const requestEvidencesReportFile = async (
  assetId: string,
  startDate: number,
  endDate: number,
  clientId: string,
  subClientId?: string,
  filters?: { [key: string]: string | number },
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(historicEvidenceFilesUrl, {
    body: { clientId, subClientId, assetId, startDate, endDate, filters },
  });

  return {
    error: response?.error,
  };
};

export const requestEvidencesByGroupReportFile = async (
  groupId: string,
  startDate: number,
  endDate: number,
  clientId: string,
  subClientId?: string,
  filters?: { [key: string]: string | number },
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(historicEvidenceFilesUrl, {
    body: { clientId, subClientId, groupId, startDate, endDate, filters },
  });

  return {
    error: response?.error,
  };
};

export const getAlarmsReports = async (
  clientId: string,
  assetId: string,
  startDate: number,
  endDate: number,
  subClientId?: string,
  alarmType?: number,
): Promise<{ alarmsReports?: HistoricAlarm[]; error?: ApiError }> => {
  const queryParams: { [key: string]: any } = {
    clientId,
    subClientId,
    startDate,
    endDate,
    alarmType,
  };
  const response = await getRequest(historicAlarmsReportUrl(assetId), {
    queryParams,
  });
  return {
    alarmsReports: response?.data,
    error: response?.error,
  };
};

export const getAlarmsReportByGroup = async (
  clientId: string,
  groupId: string,
  startDate: number,
  endDate: number,
  subClientId?: string,
  alarmType?: number,
): Promise<{ alarmsReports?: HistoricAlarm[]; error?: ApiError }> => {
  const queryParams: { [key: string]: any } = {
    clientId,
    subClientId,
    startDate,
    endDate,
    alarmType,
  };
  const response = await getRequest(historicAlarmsByGroupReportUrl(groupId), {
    queryParams,
  });
  return {
    alarmsReports: response?.data,
    error: response?.error,
  };
};

export const getAlarmsReportFiles = async (
  clientId: string,
  page: number,
  subClientId?: string,
  assetId?: string,
): Promise<{ reports?: AlarmsReport[]; error?: ApiError; total?: number }> => {
  const response = await getRequest(historicAlarmsFilesUrl, {
    queryParams: { clientId, subClientId, page, assetId },
  });

  return {
    total: response?.data?.total,
    reports: response?.data?.items,
    error: response?.error,
  };
};

export const requestAlarmsReportFile = async (
  assetId: string,
  startDate: number,
  endDate: number,
  clientId: string,
  subClientId?: string,
  filters?: { [key: string]: string | number },
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(historicAlarmsFilesUrl, {
    body: { clientId, subClientId, assetId, startDate, endDate, filters },
  });

  return {
    error: response?.error,
  };
};

export const requestAlarmsByGroupReportFile = async (
  groupId: string,
  startDate: number,
  endDate: number,
  clientId: string,
  subClientId?: string,
  filters?: { [key: string]: string | number },
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(historicAlarmsFilesUrl, {
    body: { clientId, subClientId, groupId, startDate, endDate, filters },
  });

  return {
    error: response?.error,
  };
};

export const getAlarmsReportFile = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ url?: string; error?: ApiError }> => {
  const response = await getRequest(historicAlarmsReportFileUrl(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    url: response?.data?.url,
    error: response?.error,
  };
};

export const deleteAlarmsReportFile = async (
  reportId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ error?: ApiError }> => {
  const response = await deleteRequest(historicAlarmsReportFileUrl(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    error: response?.error,
  };
};
